import React from "react";
import { Box, Typography, Chip, Tooltip, IconButton, CircularProgress, useTheme } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import { AuditStatus } from "../../../../../utils/enums";
import { getAuditListColumns } from "../../utils/auditListUtils";

const AuditListTable = ({
  audits,
  loading,
  isAuditorView,
  onArchiveAudit,
  onViewDetails,
  archivingAuditId,
}) => {
  const theme = useTheme();

  const renderCell = (row, column) => {
    switch (column.field) {
      case "framework_name":
        return (
          <Box sx={{ textAlign: "left", width: "100%", minWidth: 0, display: "flex", alignItems: "center", pl:0.5 }}>
            <Tooltip title={row.framework_name || "-"} arrow>
              <Typography
                variant="body2"
                sx={{
                  color: row.framework_name && row.framework_name !== "-"
                    ? theme.customColors.text.strong
                    : theme.customColors.text.secondary,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "wrap",
                  maxWidth: "100%",
                }}
              >
                {row.framework_name || "-"}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "audit_type":
        const auditType = row.audit_type?.toLowerCase() || "";
        const hasAuditType = auditType && auditType !== "-";
        return (
          <Box sx={{ textAlign: "left", width: "100%", minWidth: 0, display: "flex", alignItems: "center" }}>
            <Tooltip
              title={row.audit_type || "Not Assigned"}
              arrow
            >
              <Chip
                label={row.audit_type ? row.audit_type.charAt(0).toUpperCase() + row.audit_type.slice(1) : "Not Assigned"}
                size="medium"
                sx={{
                  borderRadius: "0px",
                  backgroundColor: hasAuditType ? "#1976d214" : "background.paper",
                  color: hasAuditType ? "#1976d2" : "text.secondary",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  height: "32px",
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  },
                }}
              />
            </Tooltip>
          </Box>
        );
      case "auditor_name":
        const isGenericInternal = row.audit_type === "internal" && row.auditor_name === "Internal Auditing";
        const hasMultipleAuditors = row.auditor_names_list && row.auditor_names_list.length > 1;
        const tooltipTitle = hasMultipleAuditors ? row.auditor_names_list.join(", ") : row.auditor_name;

        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:0.5 }}>
            <Tooltip title={tooltipTitle} arrow>
              <Typography
                variant={isGenericInternal ? "caption" : "body2"}
                sx={{
                  color: isGenericInternal
                    ? theme.customColors.text.secondary
                    : (row.auditor_name && row.auditor_name !== "-" && row.auditor_name !== "Not Assigned"
                    ? theme.customColors.text.strong
                      : theme.customColors.text.secondary),
                  fontSize: isGenericInternal ? "0.75rem" : "0.875rem",
                  fontStyle: isGenericInternal ? "italic" : "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.auditor_name || "-"}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "customer_name":
        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:0.25 }}>
            <Tooltip title={row.customer_name || "-"} arrow>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                fontSize: "0.875rem",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.customer_name || "-"}
            </Typography>
            </Tooltip>
          </Box>
        );
      case "audit_name":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {row.audit_name || "-"}
            </Typography>
          </Box>
        );
      case "evidence_period":
        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:1}}>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                fontSize: "0.875rem",
              }}
            >
              {row.evidence_period}
            </Typography>
          </Box>
        );
      case "total_controls":
        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:1.5 }}>
            <Typography
              variant="body2"
                sx={{
                color: theme.customColors.text.strong,
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {row.total_controls || 0}
            </Typography>
          </Box>
        );
      case "action":
        const isArchived = row.audit_status === AuditStatus.ARCHIVED;
        const isArchiving = archivingAuditId === row.id;
        
        return (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: "center" }}>
            {!isAuditorView && (
              <Tooltip title={isArchived ? "Unarchive audit" : "Archive audit"} arrow>
                <IconButton
                  size="medium"
                  onClick={() => onArchiveAudit(row)}
                  disabled={isArchiving}
                  sx={{
                    color: isArchived ? "#10B981" : "#DC2626",
                    width: 40,
                    height: 40,
                    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
                  }}
                >
                  {isArchiving ? (
                    <CircularProgress size={16} sx={{ color: isArchived ? "#10B981" : "#DC2626" }} />
                  ) : (
                    isArchived ? <UnarchiveIcon sx={{ fontSize: 20 }} /> : <ArchiveIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View Details" arrow>
              <IconButton
                size="medium"
                onClick={() => onViewDetails(row)}
                sx={{
                  color: theme.customColors?.icons?.dark || "#1E40AF",
                  width: 40,
                  height: 40,
                  "&:hover": {
                    backgroundColor: theme.palette?._blue?.light || "rgba(59, 130, 246, 0.1)",
                    transform: "translateX(2px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      default:
        return row[column.field];
    }
  };

  return (
    <ReusableTable
      columns={getAuditListColumns(isAuditorView)}
      data={audits}
      renderCell={renderCell}
      loading={loading}
      rowsPerPageOptions={[10, 50, 100]}
      rowsPerPage={50}
      sx={{
        "& .MuiTableCell-root": {
          py: 1.5,
          px: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        },
        "& .MuiTableRow-root:hover": {
          bgcolor: "rgba(0, 0, 0, 0.01)",
        },
      }}
    />
  );
};

export default AuditListTable;
